export type ManifestOption = {
  label: string;
  value: string;
};

// Canonical manifest salutation values from the API.
// Adults come back as exact "Herr" / "Frau". Non-adults can carry arbitrary
// suffix or prefix text — observed: "Kind", "Kind 1", "Kind (männlich)",
// possibly "männliches Kind". The token "Kind" / "Baby" is therefore matched
// by substring (`.includes()`), not by exact equality or `.startsWith()`.
// Salutation strings are locale-independent — server-side `translateOptions`
// translates only the option labels, never the stored values.
export enum ManifestSalutation {
  HERR = 'Herr',
  FRAU = 'Frau',
  KIND = 'Kind',
  BABY = 'Baby',
}

export type ManifestAgeBracket = 'adult' | 'kind' | 'baby' | 'unknown';

// Resolves an API salutation to its age bracket. Adult salutations are
// matched exactly; "Kind" / "Baby" use substring matching to absorb
// suffix/prefix variants the API may emit.
export const getManifestAgeBracket = (
  salutation?: string | null,
): ManifestAgeBracket => {
  if (!salutation) return 'unknown';
  if (
    salutation === String(ManifestSalutation.HERR) ||
    salutation === String(ManifestSalutation.FRAU)
  ) {
    return 'adult';
  }
  if (salutation.includes(String(ManifestSalutation.KIND))) return 'kind';
  if (salutation.includes(String(ManifestSalutation.BABY))) return 'baby';
  return 'unknown';
};

export type ManifestPersonalInfo = {
  salutation?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  title?: string | null;
};

export type ManifestContactData = {
  streetAddress?: string | null;
  zipCode?: string | null;
  city?: string | null;
  country?: string | null;
  phone?: string | null;
  mobile?: string | null;
  email?: string | null;
  approveMobilePhoneDisclosure?: boolean | null;
};

export type ManifestPassportData = {
  passportType?: string | null;
  passportNumber?: string | null;
  placeOfIssue?: string | null;
  dateOfIssue?: string | null;
  validUntil?: string | null;
  nationality?: string | null;
  placeOfBirth?: string | null;
  dateOfBirth?: string | null;
  birthName?: string | null;
  profession?: string | null;
};

export type ManifestCredentials = ManifestPassportData & {
  entryRequirementsAccepted?: boolean | null;
  // `hasVisa` is the legacy USA/ESTA confirmation checkbox. Legacy renders
  // it only when `credentials.hasVisa !== null` AND it is not a UK cruise
  // AND there is no entry-visa sub-form (India cruise). We have no UI for
  // the entry-visa sub-form yet and the backend null-vs-false signal isn't
  // surfaced through the transformer, so the field is currently inert in
  // the UI (see IdCardSection / ManifestForm initial values, validation).
  // Kept on the type for round-trip parity with the API.
  hasVisa?: boolean | null;
  hasETA?: boolean | null;
};

export type ManifestPassport = ManifestPassportData & {
  hasVisa?: boolean | null;
};

export type ManifestVisaInformation = {
  visaRequired?: boolean;
  visaText?: string | null;
  visaCategories?: Array<string>;
};

export type ManifestEmergencyContact = {
  name?: string | null;
  phone?: string | null;
};

export type ManifestDietary = {
  mealRequest?: string | null;
  individualRequest?: string | null;
  allergy?: Array<string>;
  allergyInfo?: string | null;
};

export type ManifestOtherInformation = {
  handicap?: string | null;
  emergencyContact?: ManifestEmergencyContact | null;
  pregnant24Weeks?: boolean | null;
  acceptDataProtectionTerms?: boolean | null;
  acceptHealthTerms?: boolean | null;
  dietary?: ManifestDietary | null;
  preferredLanguageCode?: string | null;
};

export type ManifestArrivalAt = {
  where?: string | null;
  time?: string | null;
};

export type ManifestDetail = {
  personalInfo?: ManifestPersonalInfo | null;
  contact?: ManifestContactData | null;
  credentials?: ManifestCredentials | null;
  entryRequirementsAccepted?: boolean | null;
  entryVisa?: ManifestPassport | null;
  visaInformation?: ManifestVisaInformation | null;
  dietary?: ManifestDietary | null;
  otherInformation?: ManifestOtherInformation | null;
  arrivalAt?: ManifestArrivalAt | null;
  preferredLanguageCode?: string | null;
};

export type ManifestPersonalInfoOptions = {
  salutationOptions?: Array<ManifestOption>;
  titleOptions?: Array<ManifestOption>;
};

export type ManifestContactDataOptions = {
  copyOptions?: Array<ManifestOption> | null;
  copyData?: {
    [key: string]: ManifestContactData;
  };
  countryOptions?: Array<ManifestOption>;
};

export type ManifestPassportOptions = {
  passportTypeOptions?: Array<ManifestOption>;
  professionOptions?: Array<ManifestOption>;
  nationalityOptions?: Array<ManifestOption>;
};

export type ManifestEntryVisaOptions = {
  passportTypeOptions?: Array<ManifestOption> | null;
  visaText?: string | null;
  visaCategories?: Array<ManifestOption>;
  // Legacy: when `indiaCruise === true` the form rendered an extra negated
  // "Bei Reiseantritt ohne Visum" checkbox tied to a full visa sub-form
  // (passportNumber / placeOfIssue / dateOfIssue / validUntil / passportType).
  // No UI for either piece in the new form — flag still flows on the type
  // for parity with the API; consumers must opt in once the visa sub-form
  // is rebuilt.
  indiaCruise?: boolean;
  ukCruise?: boolean;
};

export type ManifestDietaryOptions = {
  mealRequestOptions?: Array<ManifestOption>;
  allergyOptions?: Array<ManifestOption>;
};

export type ManifestOtherInformationOptions = {
  handicapOptions?: Array<ManifestOption>;
};

export type ManifestArrivalAtOptions = {
  copyData?: Array<unknown>;
  copyOptions?: Array<unknown>;
};

export type ManifestDetailOptions = {
  personalInfo?: ManifestPersonalInfoOptions | null;
  contact?: ManifestContactDataOptions | null;
  credentials?: ManifestPassportOptions | null;
  entryVisa?: ManifestEntryVisaOptions | null;
  dietary?: ManifestDietaryOptions | null;
  otherInformation?: ManifestOtherInformationOptions | null;
  arrivalAt?: ManifestArrivalAtOptions | null;
};

export type Manifest = {
  preliminary?: boolean;
  manifestData?: ManifestDetail | null;
  manifestOptions?: ManifestDetailOptions | null;
};

/**
 * Flat UI-shape for the full manifest form. Each section is a flat record of
 * field-name → value; nesting (dietary, emergencyContact) happens when we
 * reshape for the API on submit.
 */
export type ManifestPersonalInfoFormValues = {
  // API-supplied; effectively a `ManifestSalutation` value at runtime but
  // typed as `string` to match the Zod schema (`z.string()`) and the
  // defensive `?? ''` fallback used when the API omits it.
  salutation: string;
  title: string;
  firstName: string;
  lastName: string;
};

export type ManifestContactFormValues = {
  streetAddress: string;
  zipCode: string;
  city: string;
  country: string;
  email: string;
  phone: string;
  mobile: string;
  approveMobilePhoneDisclosure: boolean;
};

export type ManifestCredentialsFormValues = {
  passportType: string;
  nationality: string;
  passportNumber: string;
  placeOfBirth: string;
  placeOfIssue: string;
  dateOfBirth: string;
  dateOfIssue: string;
  birthName: string;
  validUntil: string;
  profession: string;
  entryRequirementsAccepted: boolean;
  hasVisa: boolean;
  hasETA: boolean;
};

export type ManifestOtherInformationFormValues = {
  mealRequest: string;
  individualRequest: string;
  allergy: string[];
  allergyInfo: string;
  handicap: string;
  emergencyContactName: string;
  emergencyContactPhone: string;
  acceptDataProtectionTerms: boolean;
  acceptHealthTerms: boolean;
  pregnant24Weeks: boolean;
  preferredLanguageCode: string;
};

export type ManifestFormValues = {
  personalInfo: ManifestPersonalInfoFormValues;
  contact: ManifestContactFormValues;
  credentials: ManifestCredentialsFormValues;
  otherInformation: ManifestOtherInformationFormValues;
};

// Shared TanStack form type for the whole manifest. Parent `ManifestForm`
// owns exactly one instance and passes it down to every section, which
// renders `form.Field` scoped to its dotted path (e.g. `personalInfo.salutation`).
// Import from @lib/form/helper: `SimpleFormApi<ManifestFormValues>`.

/**
 * Field-level errors returned by the API, keyed by section then field name.
 * Each field maps to an array of error message strings.
 */
export type ManifestFieldErrors = Record<
  string,
  Record<string, string[]> | string[]
>;
